import { PiCalendar, PiClockCounterClockwise } from 'react-icons/pi';
import React, { useCallback, useEffect } from 'react';
import { DateRange } from 'react-day-picker';
import { DateTimePickerWithRange } from './DateTimeRangePicker';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import { Button } from './Button';

interface TraillingDateTimePickerWithRangeProps {
  date: DateRange | undefined;
  setDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  timeZone: string;
  dateTimeFormat: string;
  entity: string;
  isTrailing: boolean;
  setIsTrailing: React.Dispatch<React.SetStateAction<boolean>>;
  lookBackTimeInMinutes?: number;
  onTrailingChange?: (isTrailing: boolean) => void;
  /** Override default time for the FROM date when a new date is selected */
  defaultFromTime?: { hours: number; minutes: number };
  /** Override default time for the TO date when a new date is selected. Use 'now' for current time. */
  defaultToTime?: 'now' | { hours: number; minutes: number };
}

const TrailingDateTimePickerWithRange = ({
  date,
  setDate,
  timeZone,
  dateTimeFormat,
  entity,
  isTrailing,
  setIsTrailing,
  lookBackTimeInMinutes = 60,
  onTrailingChange,
  defaultFromTime,
  defaultToTime,
}: TraillingDateTimePickerWithRangeProps) => {
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;
    if (isTrailing) {
      // Update immediately when trailing is enabled
      const updateDate = () => {
        const now = new Date();
        const from = new Date(
          now.getTime() - lookBackTimeInMinutes * 60 * 1000,
        );
        setDate({ from, to: now });
      };

      updateDate(); // Update immediately
      intervalId = setInterval(updateDate, 5000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isTrailing, setDate, lookBackTimeInMinutes]);

  const handlePresetSelect = useCallback(
    (from: Date, to: Date) => {
      setDate({ from, to });
      if (isTrailing) {
        setIsTrailing(false);
        onTrailingChange?.(false);
      }
    },
    [setDate, isTrailing, setIsTrailing, onTrailingChange],
  );

  return (
    <div className="flex space-x-2">
      <DateTimePickerWithRange
        className="w-full min-w-0"
        date={date}
        setDate={setDate}
        timeZone={timeZone}
        dateTimeFormat={dateTimeFormat}
        dateLimit="past"
        disabled={isTrailing}
        showPresets
        onPresetSelect={handlePresetSelect}
        defaultFromTime={defaultFromTime}
        defaultToTime={defaultToTime}
      ></DateTimePickerWithRange>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            size="icon"
            variant="outline"
            className="shrink-0"
            onClick={() => {
              setIsTrailing((prev: boolean) => {
                const next = !prev;
                if (!next) {
                  // Default to "Today" when switching from trailing to period mode
                  const now = new Date();
                  const from = new Date(now);
                  from.setHours(0, 0, 0, 0);
                  setDate({ from, to: now });
                }
                onTrailingChange?.(next);
                return next;
              });
            }}
          >
            {!isTrailing && (
              <PiClockCounterClockwise className="size-5"></PiClockCounterClockwise>
            )}
            {isTrailing && <PiCalendar className="size-5"></PiCalendar>}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {isTrailing
            ? `Show ${entity}s in period`
            : `Trail ${entity}s, so that new ${entity}s will pop up`}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export { TrailingDateTimePickerWithRange };
